/**
 * product-gallery.js — the product page image lightbox.
 *
 * Ported from the source's `ProductImageLightbox` React component, but built on
 * a native <dialog>. `showModal()` gives us the backdrop, the focus trap, inert
 * background content and Escape-to-close from the platform, so this file only
 * has to add what the platform does not: arrow-key paging, click-outside-to-
 * close, the "3 / 9" counter, and returning focus to the thumbnail that opened
 * the viewer (the source relied on React re-render for that; a <dialog> needs it
 * done by hand only because we call showModal() from a click handler).
 *
 * Every slide is already in the DOM (lazily loaded); paging just toggles the
 * `hidden` attribute, so there is no request latency between images.
 *
 * Binds on DOM ready and again on `shopify:section:load` so the gallery keeps
 * working when a merchant edits the section in the theme editor.
 */
(function () {
  'use strict';

  var BOUND = 'productGalleryBound';

  function setupGallery(gallery) {
    if (gallery.dataset[BOUND] === 'true') return;

    var dialogId = gallery.getAttribute('data-lightbox-target');
    if (!dialogId) return;

    var root = gallery.closest('.shopify-section') || document;
    var dialog = root.querySelector('#' + CSS.escape(dialogId));
    if (!dialog || typeof dialog.showModal !== 'function') return;

    gallery.dataset[BOUND] = 'true';

    var slides = Array.prototype.slice.call(
      dialog.querySelectorAll('[data-lightbox-slide]')
    );
    if (slides.length === 0) return;

    var counter = dialog.querySelector('[data-lightbox-counter]');
    var shell = dialog.querySelector('[data-lightbox-shell]');
    var closeButton = dialog.querySelector('[data-lightbox-close]');
    var prevButton = dialog.querySelector('[data-lightbox-prev]');
    var nextButton = dialog.querySelector('[data-lightbox-next]');

    var index = 0;
    var opener = null;

    function show(next) {
      var total = slides.length;
      index = ((next % total) + total) % total;

      slides.forEach(function (slide, i) {
        slide.hidden = i !== index;
      });

      if (counter) counter.textContent = String(index + 1);
    }

    function open(startIndex, trigger) {
      opener = trigger || null;
      show(startIndex);
      dialog.showModal();
    }

    gallery.addEventListener('click', function (event) {
      var trigger = event.target.closest('[data-gallery-open]');
      if (!trigger || !gallery.contains(trigger)) return;

      event.preventDefault();
      open(parseInt(trigger.getAttribute('data-gallery-open'), 10) || 0, trigger);
    });

    if (closeButton) {
      closeButton.addEventListener('click', function () {
        dialog.close();
      });
    }

    if (prevButton) {
      prevButton.addEventListener('click', function (event) {
        event.stopPropagation();
        show(index - 1);
      });
    }

    if (nextButton) {
      nextButton.addEventListener('click', function (event) {
        event.stopPropagation();
        show(index + 1);
      });
    }

    /**
     * Click-outside-to-close. The source closed on any click that was not on
     * the image itself; the <dialog> fills the viewport, so we close whenever
     * the click did not land on a slide or a control.
     */
    if (shell) {
      shell.addEventListener('click', function (event) {
        if (event.target.closest('.product-lightbox__slide')) return;
        if (event.target.closest('button')) return;
        dialog.close();
      });
    }

    dialog.addEventListener('keydown', function (event) {
      if (slides.length < 2) return;

      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        show(index - 1);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        show(index + 1);
      }
    });

    /** Escape (handled by the browser) and the close button both land here. */
    dialog.addEventListener('close', function () {
      if (opener && typeof opener.focus === 'function') {
        opener.focus();
      }
      opener = null;
    });
  }

  function init(scope) {
    var context = scope || document;
    var galleries = context.querySelectorAll('[data-product-gallery]');
    Array.prototype.forEach.call(galleries, setupGallery);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init(document);
    });
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
})();
